import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Link } from '@grnhse/seedling/lib/birch/typography';
import { SidePanelHandle } from 'shared/components/side_panel';
import { Row, Person, TalentMatchingApplication } from 'talent_matching/types';
import useCandidateSidePanel from 'talent_matching/hooks/useCandidateSidePanel';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import ApplicationHistoryTabPanel from 'talent_matching/components/side_panel/application_history_tab_panel';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { Row as RowComponent } from 'shared/components/birch/row';
import CandidateChips from 'talent_matching/components/shared/candidate_chips';
import LinkedinActivitySection from 'talent_matching/components/side_panel/linkedin_activity_section';
import BaseSidePanel, { CandidateName } from './base_side_panel';
import { HeaderLink } from './shared';

type Props = {
  onClose: () => void;
  applicationId: number;
  onToggle: () => void;
  isVisible?: boolean;
  rowCount?: number;
  currentIndex?: number | null;
  previousCandidate?: Row | null;
  nextCandidate?: Row | null;
  selectCandidate?: (row: Row) => void;
  candidatePrefetchBatch: Row[];
};

const TalentRediscoverySidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      selectCandidate,
      candidatePrefetchBatch,
    },
    ref
  ) => {
    const { linkedinRscEnabled } = useUserContext();
    const { activeSidePanelTab, setActiveSidePanelTab } =
      useRediscoveryTabContext();

    const { handlePanelRef, application, isLoading } = useCandidateSidePanel({
      applicationId,
      ref,
      prefetchCandidatesBatch: candidatePrefetchBatch,
      isRediscovery: true,
    });

    return (
      <BaseSidePanel
        handlePanelRef={handlePanelRef}
        application={application as TalentMatchingApplication}
        isLoading={isLoading}
        onClose={onClose}
        onToggle={onToggle}
        isVisible={isVisible}
        rowCount={rowCount}
        currentIndex={currentIndex}
        previousCandidate={previousCandidate}
        nextCandidate={nextCandidate}
        selectCandidate={selectCandidate}
        applicationId={applicationId}
        activeSidePanelTab={activeSidePanelTab}
        setActiveSidePanelTab={setActiveSidePanelTab}
        renderHeader={({ application: app }) => {
          const profileUrl = `/people/${app.personId}/applications/${app.id}`;
          return (
            <Stack spacing={1} sx={{ width: '100%' }}>
              <RowComponent spacing={1} alignItems={'center'}>
                <HeaderLink>
                  <Link href={profileUrl} target="_blank">
                    <CandidateName large>
                      <span className="pii-blur">{app.fullName}</span>
                    </CandidateName>
                  </Link>
                </HeaderLink>
              </RowComponent>
              <CandidateChips application={app as Person} showTags />
            </Stack>
          );
        }}
      >
        {({
          application: app,
          isLoading: loading,
          setLinkedinPreview,
          extendedResumePreview,
          modalDocument,
          setExtendedResumePreview,
          setModalDocument,
          allowExtendedPreview,
        }) => (
          <>
            {linkedinRscEnabled && (
              <LinkedinActivitySection
                linkedinAddress={app.linkedinAddress}
                onViewLinkedin={
                  app.linkedinIframePath
                    ? () => {
                        setLinkedinPreview(
                          app.linkedinIframePath as string | null
                        );
                      }
                    : undefined
                }
              />
            )}
            {!loading && (
              <ApplicationHistoryTabPanel
                historyUrl={app.applicationHistoryPath}
                activeDocument={extendedResumePreview ?? modalDocument ?? null}
                isRediscovery={true}
                onOpenDocument={
                  allowExtendedPreview
                    ? (doc) => {
                        setModalDocument(null);
                        setLinkedinPreview(null);
                        setExtendedResumePreview(doc);
                      }
                    : (doc) => {
                        setExtendedResumePreview(null);
                        setModalDocument(doc);
                      }
                }
              />
            )}
          </>
        )}
      </BaseSidePanel>
    );
  }
);

export default TalentRediscoverySidePanel;
